"use client";

import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

/** Underdamped on purpose: the overshoot as a letter settles is the whole effect. */
const SPRING = { type: "spring", stiffness: 460, damping: 14 } as const;

export interface SplitHoverProps {
  /** One line of headline text. Plain string, because it is split per character. */
  children: string;
  /**
   * How far a letter lifts, in em, so the travel tracks the type size instead of
   * being tuned per breakpoint. Roughly 10 to 15 percent of cap height.
   */
  lift?: number;
  /**
   * Colour a hovered letter takes. Defaults to the accent, which is the tell on
   * ink-coloured type; text that is already accent-coloured passes ink instead so
   * the hover still reads as a change.
   */
  hoverClassName?: string;
  className?: string;
}

/**
 * A line of headline split into per-letter targets that lift and tilt on hover.
 *
 * Split by word first, then by character, so a word never breaks across lines:
 * each word is an inline-block that wraps as one unit. Splitting on " " discards
 * the separator, so the space is re-inserted as its own inline-block rather than
 * left to collapse.
 *
 * Every letter is inline-block because a transform does not apply to an inline
 * box. Under reduced motion the lift is dropped and only the colour change
 * remains, which is the part that carries the affordance.
 *
 * Accessibility: the split version is aria-hidden, because a screen reader would
 * otherwise announce the line one letter at a time. The real text is carried once
 * in an sr-only span, so the accessible name of the heading stays the sentence.
 */
export const SplitHover = ({
  children,
  lift = 0.14,
  hoverClassName = "hover:text-brand-accent",
  className,
}: SplitHoverProps) => {
  const reduce = useReducedMotion();

  return (
    <>
      <span className="sr-only">{children}</span>
      <span aria-hidden className={className}>
        {children.split(" ").map((word, w, words) => (
          <span key={`${word}-${w}`} className="inline-block whitespace-nowrap">
            {[...word].map((char, c) => (
              <motion.span
                key={`${char}-${c}`}
                whileHover={reduce ? undefined : { y: `-${lift}em`, rotate: -5 }}
                transition={SPRING}
                className={cn("inline-block cursor-default transition-colors duration-200", hoverClassName)}
              >
                {char}
              </motion.span>
            ))}
            {/* split(" ") drops the separator, so put it back as its own box. */}
            {w < words.length - 1 && <span className="inline-block">&nbsp;</span>}
          </span>
        ))}
      </span>
    </>
  );
};
